import { useRef } from 'react';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import { Input, type InputProps } from '../atoms/Input';
import styles from './SearchField.module.css';

export interface SearchFieldProps
  extends Omit<InputProps, 'type' | 'startAdornment' | 'endAdornment'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible label of the clear button (i18n). */
  clearLabel: string;
}

/** Search input with icon and clear button; Escape clears (docs/ux-richtlinien.md §6.2). */
export function SearchField({
  value,
  onValueChange,
  clearLabel,
  onKeyDown,
  ...rest
}: SearchFieldProps) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <Input
      ref={ref}
      type="search"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && value) {
          e.preventDefault();
          onValueChange('');
        }
        onKeyDown?.(e);
      }}
      className={styles.search}
      startAdornment={<Icon name="search" size="sm" />}
      endAdornment={
        value ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={clearLabel}
            onClick={() => {
              onValueChange('');
              ref.current?.focus();
            }}
            iconStart={<Icon name="close" size="sm" />}
          />
        ) : undefined
      }
      {...rest}
    />
  );
}
